// @file Picks the surface search bar the Cmd/Ctrl+F shortcut should focus.
import { useSurfaceTopBarStickyReveal } from '@@/vuecomposables/useSurfaceTopBarStickyReveal'
import { nextTick } from 'vue'

/**
 * Multiple SurfaceSearchBar instances can be mounted at once (e.g. SurfaceTopBar +
 * SurfaceStickyTopBar) and all render `id="surface-search-bar"`, so getElementById cannot be
 * trusted to hit the one on screen. The sticky bar marks its instance with this attribute.
 * SurfaceStickyTopBarShell passes this same constant as the prop value, so the producer and
 * the selector cannot drift apart.
 */
const STICKY_TOP_BAR_SEARCH_BAR_OWNER = 'sticky-top-bar'
const SURFACE_SEARCH_BAR_SELECTOR = '#surface-search-bar'
const STICKY_SEARCH_BAR_SELECTOR = `#surface-search-bar[data-search-bar-owner="${STICKY_TOP_BAR_SEARCH_BAR_OWNER}"]`

/**
 * Whether any part of the search bar is actually on screen. Stricter than bits/dom's
 * isElementInViewport at the viewport edges: the auto-hidden sticky top bar parks at
 * `top: -<its own height>`, leaving its bottom edge flush with the viewport top
 * (rect.bottom === 0), which isElementInViewport counts as visible. Selecting that
 * off-screen input is exactly the TA-5298 no-op, so the boundary must read as off-screen.
 */
function isSearchBarOnScreen(searchBar: HTMLElement): boolean {
  const rect = searchBar.getBoundingClientRect()
  return (
    rect.bottom > 0 &&
    rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right > 0 &&
    rect.left < (window.innerWidth || document.documentElement.clientWidth)
  )
}

function isFocusedSurfaceSearchBarOffScreen(): boolean {
  const searchBar = document.activeElement?.closest<HTMLElement>(SURFACE_SEARCH_BAR_SELECTOR)
  if (searchBar == null) return false
  return !isSearchBarOnScreen(searchBar)
}

/**
 * Focus + select the input of the search bar the user can actually see.
 *
 * When every search bar is off-screen (scrolled past the header), reveal the sticky top bar
 * and focus its input instead. Selecting the scrolled-away top bar input focuses it without
 * scrolling it into view, which looks like Cmd/Ctrl+F did nothing (TA-5298). Focusing the
 * sticky bar's input puts the search bar in edit mode, which pauses the bar's auto-hide
 * timer for the duration of the search.
 */
function focusSurfaceSearchBarInput(): void {
  const searchBars = Array.from(document.querySelectorAll<HTMLElement>(SURFACE_SEARCH_BAR_SELECTOR))
  const onScreenSearchBar = searchBars.find(isSearchBarOnScreen)
  if (onScreenSearchBar != null) {
    onScreenSearchBar.querySelector('input')?.select()
    return
  }

  const stickySearchBarInput = document.querySelector<HTMLElement>(STICKY_SEARCH_BAR_SELECTOR)?.querySelector('input')
  if (stickySearchBarInput != null) {
    useSurfaceTopBarStickyReveal().xStickyTopBarRevealed.value = true
    void nextTick(() => stickySearchBarInput.select())
    return
  }

  // No sticky bar on this surface — scroll the first search bar into view so the shortcut
  // still visibly does something instead of focusing an off-screen input (TA-5298).
  const firstSearchBar = searchBars[0]
  firstSearchBar?.scrollIntoView({ block: 'center' })
  firstSearchBar?.querySelector('input')?.select()
}

export { STICKY_TOP_BAR_SEARCH_BAR_OWNER, focusSurfaceSearchBarInput, isFocusedSurfaceSearchBarOffScreen }
